import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Button,
    DialogActions,
    Icon,
    IconButton,
    IOption,
    Stack,
    TextField,
    Tooltip,
    Typography,
    useAppDispatch,
    useAppSelector,
    useTheme,
} from '../../../base';
import { organizationActions, organizationSelectors } from '../../../actions';
import {
    StyledDialog,
    StyledDialogCloseButton,
    StyledEmptyState,
    StyledList,
    StyledReasonText,
    StyledRow,
} from './RejectReasons.styles';

const NEW_REASON_ID = 'new';

const RejectReasons = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const dispatch = useAppDispatch();

    const rejectReasons = useAppSelector(
        organizationSelectors.getRejectApplicationReasons
    );
    const isCreateLoading = useAppSelector(
        organizationSelectors.isCreateRejectReasonLoading
    );
    const isUpdateLoading = useAppSelector(
        organizationSelectors.isUpdateRejectReasonLoading
    );
    const isDeleteLoading = useAppSelector(
        organizationSelectors.isDeleteRejectReasonLoading
    );

    const [editingId, setEditingId] = useState<string | null>(null);
    const [draftValue, setDraftValue] = useState('');
    const [reasonToDelete, setReasonToDelete] = useState<IOption | null>(null);

    const isSaving = isCreateLoading || isUpdateLoading;

    const startAdding = () => {
        setEditingId(NEW_REASON_ID);
        setDraftValue('');
    };

    const startEditing = (rejectReason: IOption) => {
        setEditingId(rejectReason.value);
        setDraftValue(rejectReason.label);
    };

    const cancelEditing = () => {
        setEditingId(null);
        setDraftValue('');
    };

    const saveDraft = () => {
        const reason = draftValue.trim();
        if (!reason) {
            return;
        }

        if (editingId === NEW_REASON_ID) {
            dispatch(organizationActions.createRejectReason({ reason }));
        } else {
            const original = rejectReasons.find(
                (rejectReason) => rejectReason.value === editingId
            );
            if (original && original.label !== reason) {
                dispatch(
                    organizationActions.updateRejectReason({
                        id: Number(editingId),
                        reason,
                    })
                );
            }
        }
        cancelEditing();
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            saveDraft();
        } else if (event.key === 'Escape') {
            cancelEditing();
        }
    };

    const confirmDelete = () => {
        if (reasonToDelete) {
            dispatch(
                organizationActions.deleteRejectReason({
                    id: Number(reasonToDelete.value),
                })
            );
        }
        setReasonToDelete(null);
    };

    const renderEditRow = (key: string) => (
        <StyledRow key={key}>
            <TextField
                autoFocus
                fullWidth
                size="small"
                value={draftValue}
                onChange={(event) => setDraftValue(event.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={t('settings.rejectReasons.placeholder')}
                inputProps={{ maxLength: 255 }}
            />
            <Tooltip title={t('settings.rejectReasons.save')}>
                <Box component="span">
                    <IconButton
                        size="small"
                        onClick={saveDraft}
                        disabled={!draftValue.trim() || isSaving}
                    >
                        <Icon
                            name="CheckIcon"
                            size={20}
                            color={theme.palette.success.main}
                        />
                    </IconButton>
                </Box>
            </Tooltip>
            <Tooltip title={t('settings.rejectReasons.cancel')}>
                <IconButton size="small" onClick={cancelEditing}>
                    <Icon
                        name="CloseIcon"
                        size={20}
                        color={theme.palette.text.secondary}
                    />
                </IconButton>
            </Tooltip>
        </StyledRow>
    );

    return (
        <Box>
            <Typography variant="h6" color={theme.palette.text.primary}>
                {t('settings.rejectReasons.title')}
            </Typography>
            <Typography
                variant="body2"
                color={theme.palette.text.secondary}
                sx={{ marginBlock: theme.spacing(1, 3) }}
            >
                {t('settings.rejectReasons.subtitle')}
            </Typography>

            <StyledList>
                {rejectReasons.length === 0 && editingId !== NEW_REASON_ID && (
                    <StyledEmptyState variant="body2">
                        {t('settings.rejectReasons.empty')}
                    </StyledEmptyState>
                )}

                {rejectReasons.map((rejectReason) =>
                    editingId === rejectReason.value ? (
                        renderEditRow(rejectReason.value)
                    ) : (
                        <StyledRow key={rejectReason.value}>
                            <StyledReasonText variant="body2">
                                {rejectReason.label}
                            </StyledReasonText>
                            <Tooltip title={t('settings.rejectReasons.edit')}>
                                <IconButton
                                    size="small"
                                    onClick={() => startEditing(rejectReason)}
                                >
                                    <Icon
                                        name="EditIcon"
                                        size={20}
                                        color={theme.palette.text.secondary}
                                    />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={t('settings.rejectReasons.delete')}>
                                <IconButton
                                    size="small"
                                    onClick={() =>
                                        setReasonToDelete(rejectReason)
                                    }
                                >
                                    <Icon
                                        name="DeleteIcon"
                                        size={20}
                                        color={theme.palette.error.main}
                                    />
                                </IconButton>
                            </Tooltip>
                        </StyledRow>
                    )
                )}

                {editingId === NEW_REASON_ID && renderEditRow(NEW_REASON_ID)}
            </StyledList>

            <Button
                variant="text"
                onClick={startAdding}
                disabled={editingId !== null}
                startIcon={<Icon name="AddRoundedIcon" size={20} />}
                sx={{ marginBlockStart: theme.spacing(2) }}
            >
                {t('settings.rejectReasons.addNew')}
            </Button>

            <StyledDialog
                open={Boolean(reasonToDelete)}
                onClose={() => setReasonToDelete(null)}
                maxWidth="xs"
                fullWidth
            >
                <StyledDialogCloseButton
                    onClick={() => setReasonToDelete(null)}
                    size="small"
                >
                    <Icon name="CloseIcon" size={22} />
                </StyledDialogCloseButton>
                <Stack
                    spacing={1.5}
                    alignItems="center"
                    sx={{ padding: theme.spacing(4, 3, 2) }}
                >
                    <Icon
                        name="DeleteIcon"
                        size={44}
                        color={theme.palette.primary.main}
                    />
                    <Typography
                        variant="h5"
                        fontWeight={700}
                        color={theme.palette.primary.main}
                    >
                        {t('settings.rejectReasons.deleteModal.title')}
                    </Typography>
                    <Typography
                        variant="body2"
                        color={theme.palette.text.secondary}
                        textAlign="center"
                    >
                        {t('settings.rejectReasons.deleteModal.message', {
                            reason: reasonToDelete?.label,
                        })}
                    </Typography>
                    <Typography
                        variant="body2"
                        color={theme.palette.text.secondary}
                        textAlign="center"
                    >
                        {t('settings.rejectReasons.deleteModal.warning')}
                    </Typography>
                </Stack>
                <DialogActions
                    sx={{
                        justifyContent: 'center',
                        paddingBlockEnd: theme.spacing(3),
                        gap: theme.spacing(1.5),
                    }}
                >
                    <Button
                        variant="outlined"
                        onClick={() => setReasonToDelete(null)}
                        sx={{ paddingInline: theme.spacing(6) }}
                    >
                        {t('settings.rejectReasons.deleteModal.cancel')}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={confirmDelete}
                        disabled={isDeleteLoading}
                        sx={{ paddingInline: theme.spacing(6) }}
                    >
                        {t('settings.rejectReasons.deleteModal.confirm')}
                    </Button>
                </DialogActions>
            </StyledDialog>
        </Box>
    );
};

export default RejectReasons;
